import { useEffect } from 'react';
import { useTheme } from '@rossum/ui/material';
import { createStoreHooks, type FeatureDeps } from '../../../../../canvas/core';
import type { PageLayoutData } from '../../../../core';
import { createPageCanvasOverlayFeature } from '../../../page-canvas-overlay';
import type { TranslationViewModel } from './useTranslationsViewModels';

type UseTranslationsCanvasRendererDeps = FeatureDeps<
  [typeof createPageCanvasOverlayFeature]
>;

const { useStore } = createStoreHooks<UseTranslationsCanvasRendererDeps>();

export const useTranslationsCanvasRenderer = (
  pageNumber: number,
  pageLayout: PageLayoutData,
  viewModels: TranslationViewModel[],
  activeRectId: string | null
) => {
  const registerRenderer = useStore(
    state => state.pageCanvasOverlay.actions.registerRenderer
  );
  const unregisterRenderer = useStore(
    state => state.pageCanvasOverlay.actions.unregisterRenderer
  );

  const theme = useTheme();

  useEffect(() => {
    const rendererId = `translations-${pageNumber}`;

    registerRenderer(
      pageLayout.pageId,
      rendererId,
      (ctx: CanvasRenderingContext2D) => {
        viewModels.forEach(vm => {
          if (vm.id === activeRectId) return;

          ctx.save();

          ctx.fillStyle = 'white';
          ctx.fillRect(vm.x, vm.y, vm.width, vm.height);

          ctx.fillStyle = 'black';
          ctx.font = `400 ${vm.fontSize}px ${theme.typography.fontFamily}, sans-serif`;
          ctx.textBaseline = 'top';
          ctx.textAlign = 'start';

          const textY = vm.y + (vm.height - vm.fontSize) / 2;
          ctx.fillText(vm.text, vm.x, textY);

          ctx.restore();
        });
      }
    );

    return () => {
      unregisterRenderer(pageLayout.pageId, rendererId);
    };
  }, [
    pageLayout.pageId,
    pageNumber,
    registerRenderer,
    unregisterRenderer,
    viewModels,
    activeRectId,
    theme.typography.fontFamily,
  ]);
};
